import { Box, Text, Divider, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Box, F0Select, F0Text } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import Link from '@factorialco/f0-react/icons/app/Link'
import { TrainingBudgetsDocument } from 'generated/resources/graphql'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ClassWizardData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import {
  SalaryCostCalculator,
  SalaryCostInfo,
} from 'modules/trainings/components/SalaryCostCalculator'

interface CostsStepProps extends WizardStepRenderParams<ClassWizardData> {
  canManageTrainingBudgets: boolean
}

export const CostsStep = ({ data, onDataChanged, canManageTrainingBudgets }: CostsStepProps) => {
  const { company } = useCurrentSession()
  const { has_dev_training_budgets } = useFeatures(['dev_training_budgets'])

  const { data: budgetsData } = useQuery(TrainingBudgetsDocument, {
    variables: {
      budgetableBy: ['training_class'],
    },
    skip: !has_dev_training_budgets,
  })

  const salaryCostInfo = new SalaryCostInfo(data.employeesSelection)
  data.trainingSessions.forEach((session) => {
    if (session.startsAt) {
      salaryCostInfo.appendFromBackend(session.startsAt, Number(session.duration) || 0)
    }
  })

  const currency = company.currency || ''

  // Budget options
  const allBudgets = budgetsData?.finance.budgetsConnection.edges || []
  const budgetOptions = allBudgets.map((edge) => ({
    value: String(edge.node.id),
    label: `${edge.node.name} (${edge.node.currency})`,
    icon: Link,
  }))

  // Calculate total cost in cents for budget info
  const totalCostCents = Math.round(
    (Number(data.cost) || 0) * 100 +
      (Number(data.indirectCost) || 0) * 100 +
      (Number(data.salaryCost) || 0) * 100
  )

  // Get selected budget info
  const selectedBudget = data.budgetId
    ? allBudgets.find((edge) => edge.node.id === data.budgetId)?.node
    : null

  const handleBudgetChange = (value: string | null) => {
    onDataChanged({ ...data, budgetId: value || null })
  }

  return (
    <Box gap="s32" paddingX="s16">
      <Box gap="s16">
        <Box gap="s8">
          <Text size="400" weight="semibold" color="grey1000">
            {i18n.t('trainings.trainings_revamp.training_class.class_wizard.costs_step.title')}
          </Text>
          <Text size="200" weight="normal">
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.costs_step.description'
            )}
          </Text>
        </Box>
        {/* Budget Section */}
        {has_dev_training_budgets && (
          <F0Box display="flex" flexDirection="column" gap="lg" paddingTop="md">
            <F0Box display="flex" flexDirection="column" gap="lg">
              <F0Text variant="label" content={i18n.t('trainings.classes.costs.budget.title')} />
              <F0Select
                label={i18n.t('trainings.classes.costs.linked_budget')}
                hideLabel
                placeholder={i18n.t('trainings.classes.costs.no_budget_linked')}
                showSearchBox
                searchBoxPlaceholder={i18n.t('common.search')}
                options={budgetOptions}
                value={data.budgetId ? String(data.budgetId) : undefined}
                onChange={handleBudgetChange}
                size="md"
                clearable
                disabled={!canManageTrainingBudgets}
              />
            </F0Box>

            {/* Budget Info Panel */}
            {selectedBudget && (
              <BudgetInfoPanel
                selectedBudget={selectedBudget}
                totalCostCents={totalCostCents}
                companyCurrency={currency}
              />
            )}
          </F0Box>
        )}
        <Divider />
        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.cost'
            )}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.cost_desc')}</Text>
          <NumberInput
            size="md"
            label={i18n.t('trainings.classes.cost_desc')}
            hideLabel
            locale={i18n.locale}
            units={currency}
            placeholder="0.00"
            onChange={(value) =>
              onDataChanged({ ...data, cost: value !== null ? String(value) : '' })
            }
            value={data.cost ? Number(data.cost) : null}
            maxDecimals={2}
          />
        </Box>
        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t('trainings.classes.indirect_cost')}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.indirect_cost_desc')}</Text>
          <NumberInput
            size="md"
            label={i18n.t('trainings.classes.indirect_cost_desc')}
            hideLabel
            locale={i18n.locale}
            units={currency}
            placeholder="0.00"
            onChange={(value) =>
              onDataChanged({ ...data, indirectCost: value !== null ? String(value) : '' })
            }
            value={data.indirectCost ? Number(data.indirectCost) : null}
            maxDecimals={2}
          />
        </Box>
        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t('trainings.classes.salary_cost')}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.salary_cost_desc')}</Text>
          <Box gap="s16" flexDirection="row" alignItems="center">
            <Box flexGrow>
              <NumberInput
                size="md"
                label={i18n.t('trainings.classes.salary_cost_desc')}
                hideLabel
                locale={i18n.locale}
                units={currency}
                placeholder="0.00"
                onChange={(value) =>
                  onDataChanged({ ...data, salaryCost: value !== null ? String(value) : '' })
                }
                value={data.salaryCost ? Number(data.salaryCost) : null}
                maxDecimals={2}
              />
            </Box>
            <SalaryCostCalculator
              salaryCostInfo={salaryCostInfo}
              onApply={(value: string) => {
                onDataChanged({ ...data, salaryCost: value })
                return true
              }}
              trackingSelectedFrom="group_creation"
              currency={currency}
            />
          </Box>
        </Box>
        <F0Alert
          variant="info"
          title={i18n.t('trainings.classes.salary_hint_result')}
          description={i18n.t('trainings.classes.salary_hint_formula')}
        />
        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.reviewed_subsidized_cost'
            )}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.subsidized_cost_desc')}</Text>
          <NumberInput
            size="md"
            label={i18n.t('trainings.classes.subsidized_cost_desc')}
            hideLabel
            locale={i18n.locale}
            units={currency}
            placeholder="0.00"
            onChange={(value) =>
              onDataChanged({ ...data, subsidizedCost: value !== null ? String(value) : '' })
            }
            value={data.subsidizedCost ? Number(data.subsidizedCost) : null}
            maxDecimals={2}
          />
        </Box>
      </Box>
    </Box>
  )
}

interface BudgetInfoPanelProps {
  selectedBudget: {
    id: string
    name: string
    availableAmountCents: number
    currency: string
  }
  totalCostCents: number
  companyCurrency: string
}

const BudgetInfoPanel = ({
  selectedBudget,
  totalCostCents,
  companyCurrency,
}: BudgetInfoPanelProps) => {
  // Suppress budget consumption calculation when currencies differ to avoid misleading percentages
  const isCurrencyMismatch = selectedBudget.currency !== companyCurrency

  if (isCurrencyMismatch) {
    return null
  }

  const availableAmountCents = Number(selectedBudget.availableAmountCents) || 0
  const consumptionPercentage =
    availableAmountCents > 0 ? Math.round((totalCostCents / availableAmountCents) * 100) : 0

  return (
    <F0Alert
      variant="info"
      description=""
      title={i18n.t('trainings.classes.costs.wizard.budget_consumption_description', {
        percentage: consumptionPercentage,
      })}
    />
  )
}
